// Scoped view over ONE shared socket.io connection (24082026 — browser
// slot-starvation fix, phase 2).
//
// Components previously each opened their OWN connection (TheHeader +
// TheSideMenu + the current page = 3+ per session). On WebSocket-blocked
// networks (corporate proxies — reproduced with
// indiapayu-backend/scripts/hostile-network-proxy.js) every one of those
// long-polls, permanently parking one of the browser's 6 per-host
// HTTP/1.1 connection slots; combined with slow API calls the whole SPA
// wedges. One shared connection = one slot, ever.
//
// The wrapper keeps every component's EXISTING call pattern working with
// safe shared semantics:
//   scope.on / scope.onAny   register + remember (for scoped cleanup)
//   scope.off(event[, h])    removes ONLY this scope's handlers — never
//                            another component's handlers for the same
//                            event name
//   scope.disconnect()       removes all THIS scope's handlers. It must
//                            NEVER disconnect the shared socket — other
//                            components are on it. (This is why the 15
//                            existing `this.socket.disconnect()` call
//                            sites keep working unchanged.)
//   scope.on('connect', h)   also fires h asynchronously when the shared
//                            socket is ALREADY connected — a component
//                            mounting late must still run its arming /
//                            room-join logic, which historically ran on
//                            its own socket's fresh connect.
export function createScopedSocket(shared) {
  const listeners = []; // [event, handler]
  const anyListeners = [];

  return {
    on(event, handler) {
      listeners.push([event, handler]);
      shared.on(event, handler);
      if (event === "connect" && shared.connected) {
        setTimeout(() => {
          // Only replay if this registration still exists (the component
          // may have cleaned up before the tick fired).
          if (listeners.some(([e, h]) => e === "connect" && h === handler)) {
            handler();
          }
        }, 0);
      }
      return this;
    },
    onAny(handler) {
      anyListeners.push(handler);
      shared.onAny(handler);
      return this;
    },
    off(event, handler) {
      for (let i = listeners.length - 1; i >= 0; i--) {
        const [e, h] = listeners[i];
        if (e === event && (handler === undefined || h === handler)) {
          shared.off(e, h);
          listeners.splice(i, 1);
        }
      }
      return this;
    },
    emit(...args) {
      shared.emit(...args);
      return this;
    },
    disconnect() {
      for (const [e, h] of listeners.splice(0)) shared.off(e, h);
      for (const h of anyListeners.splice(0)) shared.offAny(h);
      return this;
    },
    get id() {
      return shared.id;
    },
    get connected() {
      return shared.connected;
    },
  };
}
